import React, { useEffect, useState } from 'react';
import { FaCheck } from 'react-icons/fa6';
import { FaChevronRight, FaChevronDown } from 'react-icons/fa';
import { useHistory } from 'react-router';
import { ExtendedAssociation, ExtendedTeam } from '../types/types';
import { TeamMember } from '../types/generated.types';

const { REACT_APP_BRAND } = process.env;

interface SelectOption {
  value: string;
  label: string;
  slug?: string;
}

interface AssociationListProps {
  associations: ExtendedAssociation[];
}

const AssociationList: React.FC<AssociationListProps> = ({ associations }) => {
  const history = useHistory();
  const [selectedAssociation, setSelectedAssociation] = useState<SelectOption>();
  const [selectedTeam, setSelectedTeam] = useState<SelectOption>();
  const [selectedTeamMember, setSelectedTeamMember] = useState<SelectOption>();
  const [expandedAssociation, setExpandedAssociation] = useState<string | null>(null);
  const [expandedTeam, setExpandedTeam] = useState<string | null>(null);

  const placeholderText = REACT_APP_BRAND === 'CITY' ? 'Välj stad/förening' : 'Välj förening/skola';
  const noOptionsText = REACT_APP_BRAND === 'CITY' ? 'Hittade ingen stad' : 'Hittade ingen förening/skola';

  // If we only have one association, expand the teams if they exist
  useEffect(() => {
    if (associations.length === 1) {
      const hasTeams = associations[0].teams && associations[0].teams.length > 0;
      if (hasTeams) {
        setExpandedAssociation(associations[0]._id);
      }
    }
  }, [associations]);

  // Redirect - When user selects team or team member options, redirect to the correct order page
  useEffect(() => {
    // Team
    if (selectedTeamMember?.value === '0') {
      history.push({
        pathname: `/${selectedAssociation?.slug}/${selectedTeam?.slug}`,
        state: { isTeamSelected: true }
      });
    }
    // Team member
    else if (selectedAssociation && selectedTeam && selectedTeamMember) {
      history.push(`/${selectedAssociation?.slug}/${selectedTeam?.slug}/${selectedTeamMember?.slug}`);
    }
  }, [
    selectedAssociation,
    selectedTeam,
    selectedTeamMember,
    history
  ]);

  // onTeamSelect from AssociationList
  const onTeamSelect = (team, association) => {
    setSelectedAssociation({
      value: association._id,
      label: association.name,
      slug: association.slug
    });
    setSelectedTeam({
      value: team._id,
      label: team.name,
      slug: team.slug
    });
    setSelectedTeamMember({ value: '0', label: 'Lagets shop', slug: 'team' });
  };

  // Handle team member selection from AssociationList
  const onTeamMemberSelect = (teamMember, team, association) => {
    setSelectedAssociation({
      value: association._id,
      label: association.name,
      slug: association.slug
    });
    setSelectedTeam({
      value: team._id,
      label: team.name,
      slug: team.slug
    });
    setSelectedTeamMember({
      value: teamMember.value || teamMember._id,
      label: teamMember.label || `${teamMember.firstname} ${teamMember.lastname}`,
      slug: teamMember.slug || teamMember._id
    });
  };

  const onAssociationSelect = (association: SelectOption) => {
    setSelectedAssociation({ ...selectedAssociation, value: association.value, label: association.label, slug: association.slug });
    // Redirect to association page
    history.push(`/${association.slug}`);
  };

  const handleAssociationClick = (association: ExtendedAssociation) => {
    const hasTeams = association.teams && association.teams.length > 0;

    if (hasTeams) {
      if (expandedAssociation === association._id && associations.length !== 1) {
        setExpandedAssociation(null);
        setExpandedTeam(null);
      } else {
        setExpandedAssociation(association._id);
        setExpandedTeam(null);
      }
    } else {
      // No teams, select association directly
      onAssociationSelect({
        value: association._id,
        label: association.name,
        slug: association.slug ?? undefined
      });
    }
  };

  const handleTeamClick = (team: ExtendedTeam, association: ExtendedAssociation) => {
    const hasTeamMembers = team.teamMembers && team.teamMembers.length > 0;

    if (hasTeamMembers) {
      // Toggle team expansion
      if (expandedTeam === team._id) {
        setExpandedTeam(null);
      } else {
        setExpandedTeam(team._id);
      }
    } else {
      // No team members, select team directly
      if (onTeamSelect) {
        onTeamSelect(team, association);
      }
    }
  };

  const handleTeamMemberClick = (teamMember: SelectOption | TeamMember, team: ExtendedTeam, association: ExtendedAssociation) => {
    if (onTeamMemberSelect) {
      onTeamMemberSelect(teamMember, team, association);
    }
  };

  if (!associations || associations.length === 0) {
    return (
      <div className="association-list">
        <div className="association-list-items">
          <div className="association-list-empty association-item">
            {noOptionsText}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="association-list">
      <div className="association-list-header">
        {placeholderText}
      </div>
      <div className="association-list-items">
        {associations.map(association => {
          const hasTeams = association.teams && association.teams.length > 0;
          const isExpanded = expandedAssociation === association._id;

          return (
            <div key={association._id} className="association-item-wrapper">
              <div
                className={`association-item ${isExpanded ? 'expanded' : ''}`}
                onClick={() => handleAssociationClick(association)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleAssociationClick(association);
                  }
                }}
              >
                <span className="association-name">{association.name}</span>
                <div className="association-actions">
                  <span className={`association-arrow ${isExpanded ? 'expanded' : ''}`}>
                    {hasTeams ? <FaChevronDown /> : <FaChevronRight />}
                  </span>
                  {selectedAssociation?.value === association._id && (
                    <span className="association-check"><FaCheck /></span>
                  )}
                </div>
              </div>

              {/* Expanded teams list */}
              {isExpanded && hasTeams && (
                <div className="association-teams">
                  {association.teams.map(team => {
                    const hasTeamMembers = team.teamMembers && team.teamMembers.length > 0;
                    const isTeamExpanded = expandedTeam === team._id;

                    return (
                      <div key={team._id} className="team-item-wrapper">
                        <div
                          className={`team-item ${isTeamExpanded ? 'expanded' : ''}`}
                          onClick={() => handleTeamClick(team, association)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleTeamClick(team, association);
                            }
                          }}
                        >
                          <span className="team-name">{team.name}</span>
                          <div className="team-actions">
                            <span className={`team-arrow ${isTeamExpanded ? 'expanded' : ''}`}>
                              {hasTeamMembers ? <FaChevronDown size={14} /> : <FaChevronRight size={14} />}
                            </span>
                          </div>
                        </div>

                        {/* Expanded team members list */}
                        {isTeamExpanded && hasTeamMembers && (
                          <div className="team-members">
                            <div
                              className="team-member-item"
                              onClick={() => handleTeamMemberClick({ value: '0', label: 'Lagets shop' }, team, association)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleTeamMemberClick({ value: '0', label: 'Lagets shop' }, team, association);
                                }
                              }}
                            >
                              <span className="team-member-name">Lagets shop</span>
                            </div>
                            {team.teamMembers.map(teamMember => (
                              <div
                                key={teamMember._id}
                                className="team-member-item"
                                onClick={() => handleTeamMemberClick(teamMember, team, association)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleTeamMemberClick(teamMember, team, association);
                                  }
                                }}
                              >
                                <span className="team-member-name">
                                  {teamMember.firstname} {teamMember.lastname}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AssociationList;
